import { Component, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthService } from './auth.service';
import { AppRoute, LoadUserResult, LogoutReason } from '@shared/constants';
import { PulseLoadingScreenComponent } from '@shared/components/loading-screen';
import { ENVIRONMENT_CONFIG, EnvironmentConfig } from '@shared/config';
import { applyPreferredLocaleIfNeeded } from '@shared/i18n';
import { clearAuthReturnUrl, clearPendingEntityRelationshipId, consumeAuthReturnUrl, resolvePendingEntityRelationshipId } from '@shared/utils';
import { tap } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'pulse-auth',
  imports: [PulseLoadingScreenComponent],
  template: '<pulse-loading-screen />'
})
export class AuthComponent {
  private authService = inject(AuthService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private envConfig = inject<EnvironmentConfig>(ENVIRONMENT_CONFIG);

  constructor() {
    this.authService.isLoading$.pipe(takeUntilDestroyed()).subscribe((isLoading) => {
      console.log('[AUTH] isLoading: ', isLoading);
      if (!isLoading) {
        if (this.envConfig.localeSwitchEnabled && applyPreferredLocaleIfNeeded()) {
          console.log('[AUTH] Redirecting to preferred locale bundle');
          return;
        }

        console.log('[AUTH] Auth0 finished loading, loading user data');
        this.loadUserData();
      }
    });
  }

  private loadUserData() {
    const selectedEntityRelationshipId = resolvePendingEntityRelationshipId(this.route.snapshot.queryParamMap.get('entityRelationshipId'));
    const shouldForceReload = !!selectedEntityRelationshipId;

    // Check if user data is already loaded
    const user = this.authService.user();
    if (user.isInitialized && !shouldForceReload) {
      console.log('[AUTH] User data already loaded, restoring previous page');
      this.navigateAfterUserReady();
      return;
    }

    if (shouldForceReload) {
      clearAuthReturnUrl();
      this.authService.clearUser(LogoutReason.SwitchingAccountContext);
    }

    // Load user from database
    this.authService
      .loadUser(selectedEntityRelationshipId)
      .pipe(
        takeUntilDestroyed(),
        tap((result) => {
          clearPendingEntityRelationshipId();

          if (result === LoadUserResult.Loaded) {
            console.log('[AUTH] User loaded successfully, restoring previous page');
            this.navigateAfterUserReady();
            return;
          }

          if (result === LoadUserResult.AuthSessionFailed) {
            console.error('[AUTH] Auth session failed while loading user; returning to login');
            this.authService.logout(LogoutReason.LoginRequired);
            return;
          }

          console.error('[AUTH] Failed to load user data');
          this.authService.logout(LogoutReason.UserDataFetchFailed, AppRoute.LoginUnsuccessful);
        })
      )
      .subscribe();
  }

  /**
   * Returns to the page the user was on, or the default home route when none was stashed.
   */
  private navigateAfterUserReady(): void {
    const returnUrl = consumeAuthReturnUrl();
    void this.router.navigateByUrl(returnUrl ?? '/');
  }
}
